import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BalanceSheet } from '@models/balance-sheet';
import { HoverNotification } from '@models/hover-notification';
import { RetrievedBalanceSheet } from '@models/retrieved-balance-sheet';
import { AppConfigAPI } from '@models/types/appconfig.d';
import { UserEditTrackingItem } from '@models/user-edit-tracking-item';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { DatasetsService } from '@services/datasets/datasets.service';
import { TrackingService } from '@services/tracking/tracking.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class BalanceSheetService {
  private environment: AppConfigAPI;

  get invalidRows() {
    return this.invalidRows$.getValue();
  }
  set invalidRows(rows: number[]) {
    if (!isEqual(rows, this.invalidRows)) {
      this.invalidRows$.next(rows);
    }
  }

  get hoverNotification(): HoverNotification {
    return this.hoverNotification$.value;
  }
  set hoverNotification(notif: HoverNotification) {
    this.hoverNotification$.next(notif);
  }

  get data(): BalanceSheet[] {
    return this.dataChange.value;
  }

  set data(newData: BalanceSheet[]) {
    if (newData && typeof newData === 'object' && newData.length > 0) {
      this.dataChange.next(newData);
    }
  }

  get validationFeedback() {
    return this.invalidRows.length > 0 ? `Validation errors in rows\n${this.invalidRows.toString()}` : '';
  }

  constructor(
    private http: HttpClient,
    public toasterService: NotificationsService,
    private trackingService: TrackingService,
    private datasetsService: DatasetsService,
    private appConfig: AppConfigProvider
  ) {
    this.appConfig.environment.subscribe(env => {
      this.environment = env.API;
    });
  }

  public subscriptions: Subscription[] = [];
  dataChange = new BehaviorSubject<BalanceSheet[]>(null);
  data$ = this.dataChange.asObservable();

  public validatingRows = true;
  public invalidRows$ = new BehaviorSubject<number[]>([]);

  public editingUsers = <any>[];
  public editingUsersList = '';

  public hoverNotification$ = new BehaviorSubject<HoverNotification>({});

  // store temporarily
  public temporaryStoreBalanceSheet(data: UserEditTrackingItem): Observable<Object> {
    const url = `${this.environment.TRACKINGAPI}/api/tracking/balancesheet`;
    return this.http.post(url, data).pipe(
      tap(() => {
        this.trackingService.updateModelDataChangesFlags('balanceSheet', true, `Balance Sheet edit tracking item saved`);
      })
    );
  }

  // store permanently
  public async permanentlyStoreBalanceSheet(json: string) {
    // Store permanent copy
    const balanceSheetUrl = `${this.environment.BALANCEAPI}/api/balancesheet`;
    const data = {
      json,
      user_id: -1,
    };
    const storedBalanceSheet: any = await this.http
      .post(balanceSheetUrl, data)
      .toPromise()
      .catch(err => false);
    // The back-end deletes the associated tracking data

    if (storedBalanceSheet) {
      // Delete tracking data
      const trackingUrl = `${this.environment.TRACKINGAPI}/api/tracking/balancesheet/delete`;
      const deletedTrackingItem = await this.http
        .get(trackingUrl)
        .toPromise()
        .catch(err => false);

      // Update notifications
      this.datasetsService.getRequiredDatasetImport();
      this.trackingService.updateModelDataChangesFlags(
        'balanceSheet',
        false,
        `Balance sheet tracking set to FALSE after saving balance sheet`
      );
    }
    return storedBalanceSheet;
  }

  public async getBalanceSheets() {
    // Check if there is tracking records
    const trackingUrl = `${this.environment.TRACKINGAPI}/api/tracking/balancesheet`;
    let trackingRecord = <any>await this.http.get<RetrievedBalanceSheet>(trackingUrl).toPromise();

    if (!trackingRecord.isEdited || !trackingRecord.dataItems) {
      trackingRecord = false;
    }
    this.trackingService.updateModelDataChangesFlags('balanceSheet', trackingRecord, `Balance Sheet has edited fields: ${trackingRecord}`);

    return await (trackingRecord ? trackingRecord : this.getPermanentBalanceSheetData());
  }

  public getBalanceSheetsDate() {
    const url = `${this.environment.TRACKINGAPI}/api/tracking/balancesheet/lastchange`;
    return this.http.get(url);
  }

  public async getEditingUsers() {
    // Get the users that are editing the sheet
    const result = await this.trackingService.getEditingUsers('BalanceSheetStructure', null);

    // Set the values
    this.editingUsers = result;
    this.editingUsersList = result;
  }

  // Executed when the user clicks load latest
  public async loadLatest() {
    const retrievedBalanceSheet = await this.getPermanentBalanceSheetData();
    // 1. Delete the tracking record
    this.deleteTrackingItems();
    // 2. Load the data
    return retrievedBalanceSheet;
  }

  public async getBalanceSheetSavedAccounts() {
    const balanceSheetData = (await this.getPermanentBalanceSheetData()) as RetrievedBalanceSheet;
    if (balanceSheetData?.dataItems) {
      return balanceSheetData.dataItems.map(data => data.account_number);
    }
    return [];
  }

  public async getBalanceSheetSavedAccountMnemonics() {
    const balanceSheetData = (await this.getPermanentBalanceSheetData()) as RetrievedBalanceSheet;
    if (balanceSheetData?.dataItems) {
      return balanceSheetData.dataItems.map(data => data.mnemonic);
    }
    return [];
  }

  private async getPermanentBalanceSheetData() {
    const balanceSheetUrl = `${this.environment.BALANCEAPI}/api/balancesheet`;
    return await this.http
      .get(balanceSheetUrl)
      .toPromise()
      .catch(err => false);
  }

  public async deleteTrackingItems() {
    // 1. Delete the tracking record
    const trackingUrl = `${this.environment.TRACKINGAPI}/api/tracking/balancesheet/delete`;

    return await this.http
      .get(trackingUrl)
      .pipe(
        tap(() => {
          this.trackingService.updateModelDataChangesFlags('balanceSheet', false, `Delete balance sheet tracking items`);
        })
      )
      .toPromise()
      .catch(err => false);
  }

  // For an specific user (not the user that is requesting it) -- used from user-list
  // (so it should be moved to the component or service related)
  public deleteAllTrackingItems(userId: number) {
    const url = `${this.environment.TRACKINGAPI}/api/tracking/balancesheet/delete?deleteAll=1`;
    return this.http.get(url);
  }

  public getCategories() {
    const categoriesUrl = `${this.environment.BALANCEAPI}/api/balancesheet/categories`;
    return this.http.get(categoriesUrl);
  }
}
